import { globalEmitter, emitterProps } from '@/components/helpers/emitter-event'
import { swymService } from 'libs/swym/swym-service'
import { SIZE_SELECTED_CPROPS, isSizeSelected } from 'libs/swym/swym-entry'

/**
 * PDP wishlist toggle. Reads the selected variant from the product form's
 * `[name="id"]` field, falling back to the sticky ATC bar's own `[name="id"]`
 * (via `data-pair-form-id`) since variant selection only mirrors main → sticky.
 *
 * Saving is size-specific: with no size selected the heart shows product-level
 * state and a click clears every saved size (or prompts for a size); with a
 * size selected it toggles that size only. Saving a size also clears any
 * product-level entry left over from a PLP heart.
 */
export class WishlistToggle extends HTMLElement {
  private button: HTMLButtonElement | null = null
  private busy = false
  // Cached: a partial AJAX re-render can swap in a new form host node while
  // this element stays connected, and the `change` listener must be removed
  // from the same node it was added to.
  private formHostElement: HTMLElement | null = null

  private get productId(): number {
    return Number(this.dataset.productId)
  }

  connectedCallback(): void {
    this.button = this.querySelector('[data-wishlist-toggle]')
    this.button?.addEventListener('click', this.onClick)
    this.formHostElement = this.formHost()
    // `change` bubbles from the form's `[name="id"]` select — both the native
    // event and the synthetic one variant-selector.ts dispatches on selection.
    this.formHostElement?.addEventListener('change', this.onFormChange)
    globalEmitter.on(emitterProps.wishlist.change, this.syncSaved)
    this.syncSaved()
  }

  disconnectedCallback(): void {
    this.button?.removeEventListener('click', this.onClick)
    this.formHostElement?.removeEventListener('change', this.onFormChange)
    this.formHostElement = null
    globalEmitter.off(emitterProps.wishlist.change, this.syncSaved)
  }

  private onClick = async (event: Event): Promise<void> => {
    event.stopPropagation()
    if (this.busy) return

    const variantId = this.selectedVariantId()
    const saved = this.isSaved()

    // No size chosen: product-level view. Nothing saved yet means there is
    // nothing to toggle, so prompt for a size instead of guessing one.
    if (!variantId) {
      if (!saved) {
        this.showSizePrompt()
        return
      }
      this.busy = true
      const removed = await swymService.removeProduct(this.productId)
      if (removed) this.hideStatus()
      else this.showStatus('error')
      this.busy = false
      return
    }

    this.busy = true
    // `cprops` is what marks this as a size the shopper chose: every entry stores
    // a real `epi`, including product-level PLP saves that defaulted to one.
    const payload = {
      epi: variantId,
      empi: this.productId,
      du: this.productUrl(),
      cprops: SIZE_SELECTED_CPROPS
    }
    if (saved) {
      const removed = await swymService.removeVariant(payload)
      if (removed) this.hideStatus()
      else this.showStatus('error')
    } else {
      const added = await swymService.add(payload)
      this.showStatus(added ? 'success' : 'error')
      if (added) await this.clearOtherProductLevelEntries(variantId)
    }
    this.busy = false
  }

  /**
   * A size-specific save supersedes the product-level entry (no `cprops`,
   * e.g. a PLP heart), so remove it — but only when there is exactly one.
   * Normal use can't produce two or more cprops-less entries for a product,
   * so 2+ means `cprops` was dropped somewhere (e.g. Swym's guest→login list
   * merge); deleting then would silently discard saved sizes, so leave them.
   */
  private async clearOtherProductLevelEntries(variantId: number): Promise<void> {
    const stale = swymService
      .entriesFor(this.productId)
      .filter((entry) => !isSizeSelected(entry) && Number(entry.epi) !== variantId)

    if (stale.length !== 1) return

    await swymService.removeVariant(stale[0])
  }

  /**
   * With a size selected the heart tracks that variant; without one it falls
   * back to "any size of this product is saved" so a returning shopper still
   * sees their save.
   */
  private isSaved(): boolean {
    const variantId = this.selectedVariantId()
    return variantId ? swymService.hasVariant(variantId) : swymService.has(this.productId)
  }

  private syncSaved = (): void => {
    this.setSaved(this.isSaved())
  }

  private onFormChange = (event: Event): void => {
    const target = event.target as HTMLInputElement | HTMLSelectElement | null
    if (target?.name !== 'id') return
    this.syncSaved()
  }

  /**
   * `data-form-id` matches the `id` on the product form host element
   * (`enhanced-product-form`/`gift-card-form`).
   */
  private formHost(): HTMLElement | null {
    const formId = this.dataset.formId
    return formId ? document.getElementById(formId) : null
  }

  /**
   * variant-selector.ts only propagates main → sticky, so a size picked only
   * in the sticky bar never reaches the main form's `[name="id"]` — fall back
   * to reading the sticky form's own field. One-off read at click/state-check
   * time, not a live sync. Both reads are gated by `formHasResolvedVariant()`
   * because the raw value alone can't be trusted (see that method).
   */
  private selectedVariantId(): number | null {
    if (this.formHasResolvedVariant(this.dataset.formId)) {
      const value = this.formHost()?.querySelector<HTMLInputElement | HTMLSelectElement>('[name="id"]')?.value
      if (value) return Number(value)
    }
    if (this.formHasResolvedVariant(this.dataset.pairFormId)) {
      const stickyValue = this.pairFormHost()?.querySelector<HTMLInputElement | HTMLSelectElement>('[name="id"]')?.value
      if (stickyValue) return Number(stickyValue)
    }
    return null
  }

  /**
   * The hidden `select[name="id"]` can hold a real variant id at page load with
   * no chip showing as selected (`variant.selected` and `value.selected` can
   * disagree in the grid/hidden selectors), so the value alone can't be trusted.
   * Use the signal that already gates the ATC button: the form's wrapping
   * `<x-modal data-id="…">` carries `data-ui="visible"` or `"disabled"` only
   * once a variant is genuinely resolved. `"disabled"` (sold out, pre-release)
   * still counts — saving a sold-out size is allowed.
   * Relies on `registerCustomElements.ts` defining `x-modal` before
   * `variant-selector`, so `data-ui` is already set when read at click time.
   */
  private formHasResolvedVariant(formId: string | undefined): boolean {
    if (!formId) return false
    // Resolve from this element upward first (the main form's `<x-modal>` is
    // an ancestor of the toggle), falling back to a document-wide lookup for
    // the sticky pair form, which lives outside the main form entirely.
    // Equivalent today either way — the id is unique on the page — but this
    // ordering would still resolve correctly if a page ever rendered two
    // product forms.
    const modal =
      this.closest<HTMLElement>(`x-modal[data-id="${formId}"]`) ??
      document.querySelector<HTMLElement>(`x-modal[data-id="${formId}"]`)
    return modal?.dataset.ui === 'visible' || modal?.dataset.ui === 'disabled'
  }

  /**
   * The sticky ATC form's host element, identified by `data-pair-form-id`.
   * Not every template renders a sticky bar, so a missing attribute or
   * element is a silent no-op.
   */
  private pairFormHost(): HTMLElement | null {
    const pairFormId = this.dataset.pairFormId
    return pairFormId ? document.getElementById(pairFormId) : null
  }

  private productUrl(): string {
    return this.dataset.productUrl ?? window.location.href.split('?')[0]
  }

  /**
   * Reuses the ATC "Select Size" warning modal rather than adding a second
   * message. No hide logic needed — the modal carries
   * `group-data-visible/form:hidden`, so choosing a size hides it.
   */
  private showSizePrompt(): void {
    const modal = document.querySelector<HTMLElement & { handleShow?:() => void }>(
      'x-modal[data-id="wishlist-message"]'
    )
    if (!modal) return
    if (typeof modal.handleShow === 'function') modal.handleShow()
    else modal.setAttribute('data-ui', 'visible')
  }

  /**
   * `[data-id="wishlist-status"]` lives alongside the ATC buttons inside the
   * same product-form host (`data-form-id`), not inside this element.
   */
  private statusElement(): HTMLElement | null {
    return this.formHost()?.querySelector<HTMLElement>('[data-id="wishlist-status"]') ?? null
  }

  private showStatus(status: 'success' | 'error'): void {
    const el = this.statusElement()
    if (!el) return
    const message = status === 'success' ? el.dataset.wishlistSuccessMessage : el.dataset.wishlistErrorMessage
    const messageEl = el.querySelector('[data-wishlist-status-message]')
    if (messageEl) messageEl.textContent = message ?? ''
    el.setAttribute('data-status', status)
    el.setAttribute('data-ui', 'visible')
  }

  private hideStatus(): void {
    const el = this.statusElement()
    if (!el) return
    el.removeAttribute('data-ui')
    el.removeAttribute('data-status')
  }

  private setSaved(saved: boolean): void {
    if (saved) {
      this.button?.setAttribute('data-saved', '')
      this.button?.setAttribute('aria-label', 'Remove from Wishlist')
    } else {
      this.button?.removeAttribute('data-saved')
      this.button?.setAttribute('aria-label', 'Add to Wishlist')
    }
  }
}
